import { useEffect, useRef, useState } from "react";
import type { WebShopPublicUser } from "@/api_client";
import { useContent } from "@/hooks/api/content/useContent";
import { handleEntryProductPage } from "@/web_analytics/dataLayer/pageLoaded/pageLoadedEmitters";
import { useSetCustomerContext } from "../../hooks/api/customer_context/useSetCustomerContext";
import { useOffer } from "../../hooks/api/offer/useOffer";
import { useAddProduct } from "../../hooks/api/product/useAddProduct";
import { useUser } from "../../hooks/api/useUser";
import { findQuote } from "../../utils/productUtils";

// Inline type references (avoid top-level type-only imports per lint rules)
type Channel = import("@/api_client").Channel;
type ProductType = import("@/api_client").ProductType;

type UseEntryCommonParams = {
  productType: ProductType; // e.g. 'travel' | 'householder'
  backendHost?: string;
  appPathPrefix?: string;
  skipCart: boolean;
  channel?: Channel;
  contentPageKey: string; // e.g. 'travelEntryPage' | 'householderEntryPage'
  commonContentPageKey?: string; // e.g 'householderCommonPage'
  language?: string;
};

// Inline client type references
type Quote = import("@/api_client").Quote;
type AddQuoteResult = import("@/api_client").AddQuoteResult;
type PolicyHolder = import("@/api_client").PolicyHolder | null | undefined;

interface UseEntryCommonReturn<TContent, TCommonContent> {
  isLoading: boolean;
  isSubmitting: boolean;
  submitEntryHandler: (quote: Quote) => Promise<AddQuoteResult>;
  policyHolder: PolicyHolder;
  user: WebShopPublicUser | undefined;
  content?: TContent | null;
  commonContent?: TCommonContent | null;
}

/**
 * Shared logic for product entry web components.
 * Handles loading state aggregation, submit flow, and content patching.
 */
export function useEntryForWebComponents<TContent, TCommonContent = null>({
  productType,
  backendHost,
  appPathPrefix,
  skipCart,
  channel,
  language,
  contentPageKey,
  commonContentPageKey,
}: UseEntryCommonParams): UseEntryCommonReturn<TContent, TCommonContent> {
  const { data: offer, isLoading: isLoadingOffer } = useOffer();
  const { mutateAsync: setCustomerContext } = useSetCustomerContext();

  const { data: content, isLoading: isLoadingContent } = useContent(
    true,
    language,
  );
  const { data: user, isLoading: isLoadingUser } = useUser();

  const {
    mutateAsync: addProduct,
    isPending: addProductPending,
    isSuccess: addProductSuccess,
  } = useAddProduct();

  // Track the in-flight customer context mutation so we can await it before submit
  const customerContextPromiseRef = useRef<Promise<void> | null>(null);
  const [isSubmittingChain, setIsSubmittingChain] = useState(false);

  // fire customer context set as soon as possible, we have the payload data from the start
  // this is so that by the time user submits, the context is already set and we don't add extra wait time to submit
  useEffect(() => {
    async function setCustomerContextAndPageLoadAsync() {
      const payload = { skipCart, channel, language: language ?? "fi" };
      const customerContext = await setCustomerContext({ body: payload });
      // Emit page loaded event for entry product page needs the customer context
      handleEntryProductPage({
        productType,
        channel: channel ?? "WS",
        customerContext: customerContext || {},
        contextId: customerContext?.publicId ?? "",
      });
    }
    // store the promise so we can await it in submit so we are sure it's done before submit
    customerContextPromiseRef.current = setCustomerContextAndPageLoadAsync();
  }, [setCustomerContext, skipCart, channel, language, productType]);

  const isLoading = isLoadingContent || isLoadingOffer || isLoadingUser;
  const isSubmitting =
    isSubmittingChain || addProductPending || addProductSuccess;

  const submitEntryHandler = async (quote: Quote): Promise<AddQuoteResult> => {
    setIsSubmittingChain(true);
    // Ensure customer context is set before adding the product
    // we fire this as soon as possible in useEffect above, but await here to be sure,
    // this will save us the time taken for that call when submitting the entry
    if (customerContextPromiseRef.current) {
      try {
        await customerContextPromiseRef.current;
      } catch {
        // Swallow errors to avoid blocking submit;
      }
    }
    const added = await addProduct(quote);
    if (backendHost) {
      const foundQuote = findQuote(added.offer, added?.id);
      const route =
        foundQuote?.priceCalculatedAt !== undefined &&
        foundQuote?.priceCalculatedAt !== null
          ? "price"
          : "edit";

      let normalizedBackendHost = backendHost;
      while (normalizedBackendHost.endsWith("/")) {
        normalizedBackendHost = normalizedBackendHost.slice(0, -1);
      }

      let normalizedAppPathPrefix = appPathPrefix ?? "";
      while (normalizedAppPathPrefix.startsWith("/")) {
        normalizedAppPathPrefix = normalizedAppPathPrefix.slice(1);
      }
      while (normalizedAppPathPrefix.endsWith("/")) {
        normalizedAppPathPrefix = normalizedAppPathPrefix.slice(0, -1);
      }
      const redirectPathPrefix = normalizedAppPathPrefix
        ? `/${normalizedAppPathPrefix}`
        : "";

      globalThis.location.href = `${normalizedBackendHost}${redirectPathPrefix}/${productType}/${route}?id=${added.id}&itm_ws_source=prpg`;
    }
    setIsSubmittingChain(false);
    return added;
  };

  const rawContent = content as unknown as Record<string, unknown> | undefined;
  const rawPageContent = rawContent?.[contentPageKey] as TContent;

  let commonContent: TCommonContent | undefined;
  if (commonContentPageKey) {
    commonContent = rawContent?.[commonContentPageKey] as TCommonContent;
  }

  return {
    isLoading,
    isSubmitting,
    submitEntryHandler,
    policyHolder: offer?.policyHolder,
    user,
    content: rawPageContent,
    commonContent,
  };
}
